export const PAGE_TYPES = [
    'home',
    'search_video',
    'search_audio',
    'search_image',
    'search_template',
    'detail_video',
    'detail_audio',
    'detail_image',
    'category',
    'pricing',
    'sign_up',
    'ai',
    'blog',
    'other',
] as const;

export const CTA_DESTINATIONS = ['pricing', 'checkout', 'sign_up', 'log_in'] as const;

export type PageType = (typeof PAGE_TYPES)[number];
export type CtaDestination = (typeof CTA_DESTINATIONS)[number];

export const UNNAMED_PLACEMENT = 'unnamed';

export const CTA_TAG_PATTERN = new RegExp(
    `^cta\\.(${PAGE_TYPES.join('|')})\\.[a-z0-9_]{1,64}\\.(${CTA_DESTINATIONS.join('|')})\\.click$`,
);

// Source: StockBlocks app/Admin/Constants/PageTypeRegex.php and app/Search/Routes/*SearchRoutes.php
const MEDIA_SEGMENTS: Record<string, 'video' | 'audio' | 'image' | 'template'> = {
    video: 'video',
    'all-video': 'video',
    'motion-graphics': 'video',
    audio: 'audio',
    images: 'image',
    templates: 'template',
};

const NON_CATEGORY_MEDIA_SUBPATHS = new Set(['upgrade-my-plan']);

export function pageTypeForPath(pathname: string, search = ''): PageType {
    const [first, ...rest] = pathname.toLowerCase().split('/').filter(Boolean);
    const second = rest[0];

    if (first === undefined) return 'home';
    if (first === 'pricing') return 'pricing';
    if (first === 'sign-up') return 'sign_up';
    if (first === 'ai') return 'ai';
    if (first === 'blog') return 'blog';

    const media = MEDIA_SEGMENTS[first];
    if (media === undefined || second === undefined) return 'other';
    if (second === 'stock' || second.startsWith('stock-')) return media === 'template' ? 'other' : `detail_${media}`;
    if (isQueryStringCategory(media, rest, search)) return 'category';
    if (rest.slice(0, 2).includes('search')) return `search_${media}`;
    return NON_CATEGORY_MEDIA_SUBPATHS.has(second) ? 'other' : 'category';
}

function isQueryStringCategory(media: string, pathAfterMedia: string[], search: string): boolean {
    if (media !== 'audio' && media !== 'image') return false;
    if (pathAfterMedia.length !== 1 || pathAfterMedia[0] !== 'search') return false;

    const params = new URLSearchParams(search);
    return Boolean(params.get('media-type')) && Boolean(params.get('categories'));
}

export function ctaDestinationForPath(pathname: string): CtaDestination | undefined {
    const path = pathname.toLowerCase();

    if (isPathOrChild(path, '/pricing') || path.includes('plans-and-pricing')) return 'pricing';
    if (isPathOrChild(path, '/sign-up/checkout')) return 'checkout';
    if (isPathOrChild(path, '/sign-up')) return 'sign_up';
    if (isPathOrChild(path, '/login')) return 'log_in';
    return undefined;
}

export function sanitizePlacement(rawPlacement: string | undefined): string {
    const placement = (rawPlacement ?? '')
        .toLowerCase()
        .replace(/-/g, '_')
        .replace(/[^a-z0-9_]/g, '');
    return placement === '' ? UNNAMED_PLACEMENT : placement;
}

export function resolveCtaTag(
    target: EventTarget | null,
    location: Pick<Location, 'href' | 'origin' | 'pathname' | 'search'>,
): string | undefined {
    const anchor = target instanceof Element ? target.closest('a[href]') : null;
    if (anchor === null) return undefined;

    const destination = destinationOfAnchor(anchor, location);
    if (destination === undefined) return undefined;

    const placement = sanitizePlacement(anchor.closest<HTMLElement>('[data-cta]')?.dataset.cta);
    return `cta.${pageTypeForPath(location.pathname, location.search)}.${placement}.${destination}.click`;
}

function destinationOfAnchor(anchor: Element, location: Pick<Location, 'href' | 'origin'>): CtaDestination | undefined {
    try {
        const url = new URL(anchor.getAttribute('href') ?? '', location.href);
        return url.origin === location.origin ? ctaDestinationForPath(url.pathname) : undefined;
    } catch {
        return undefined;
    }
}

function isPathOrChild(path: string, base: string): boolean {
    return path === base || path.startsWith(`${base}/`);
}
